import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Appointments } from '@gql/base-types';
import { useGetAppointmentSubscription } from '../queries/getAppointment.subscription.generated';
import { useRooftop } from '@store';
import { useProductEntry } from '@features/products/useProductEntry';
import { isProductHeld } from '@features/products/productGates';
import type { Appointments as AppointmentsConfig } from '@hopdrive/sdk-configs';
import { CONSUMER_PAY_DISABLED_REASON, CONSUMER_PAY_ENABLED } from '@hopdrive/sdk-appointments';
import { DealerMovePlannerProvider } from '@features/moveCreation/plannerMount';
import DefaultFallback from '@components/Fallbacks/DefaultFallback';
import Loading from '@components/Utils/Loading';
import AppointmentAddForm from '../components/AppointmentAddForm.tsx';
import AppointmentDetailRouter from '../components/AppointmentDetailRouter';
import { useParams, useLocation } from 'react-router-dom';
import { css } from '@emotion/css';
import { Theme, useTheme, Container } from '@mui/material';

const AppointmentDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const isNewAppointment = location.pathname === '/appointments/add';
  const appointmentId = isNewAppointment ? null : id ? parseInt(id) : null;
  const rooftop = useRooftop();
  const mobileServiceHeld = isProductHeld(useProductEntry('mobile-service'));
  const theme = useTheme();
  const styles = useStyles(theme);

  const [accessAllowed, setAccessAllowed] = useState(false);

  const { data, loading, error } = useGetAppointmentSubscription({
    variables: { id: appointmentId! },
    skip: appointmentId === null,
  });

  useEffect(() => {
    // A rooftop holding Mobile Service can legitimately have NO valet
    // appointments.enabled flag set. Either path unlocks the detail page —
    // Hasura RLS is the authoritative security layer; this is just a UI
    // feature gate. Until the products load, Mobile Service reads as not held.
    const valetEnabled =
      (rooftop?.effective_config as { appointments?: AppointmentsConfig } | undefined)?.appointments?.enabled === true;
    setAccessAllowed(valetEnabled || mobileServiceHeld);
  }, [rooftop, mobileServiceHeld]);

  if (loading) {
    return <Loading />;
  }

  if (!accessAllowed) {
    return <DefaultFallback fullscreen severity='error' message='RESTRICTED ACCESS' />;
  }
  // The add form creates a consumer-pay offer. A bookmarked or typed
  // /appointments/add still lands here, so refuse it on the page too.
  if (isNewAppointment && !CONSUMER_PAY_ENABLED) {
    return <DefaultFallback fullscreen severity='warning' message={CONSUMER_PAY_DISABLED_REASON} />;
  }
  // If this is a new appointment, render the form immediately
  if (isNewAppointment) {
    return (
      <div>
        <DealerMovePlannerProvider state={{ appointment: (data?.appointments_by_pk as Appointments) || {} }}>
          <div className={styles.pageContainer}>
            <Container maxWidth='lg'>
              <AppointmentAddForm />
            </Container>
          </div>
        </DealerMovePlannerProvider>
      </div>
    );
  }

  if (error) {
    return (
      <Sentry.ErrorBoundary
        fallback={<DefaultFallback fullscreen severity='error' message='Error fetching appointment details!' />}
      />
    );
  }

  if (data?.appointments_by_pk) {
    return (
      <div className={styles.pageContainer}>
        <AppointmentDetailRouter appointment={data.appointments_by_pk as Appointments} />
      </div>
    );
  }

  return <DefaultFallback fullscreen severity='warning' message='No appointment found!' />;
};

const useStyles = (theme: Theme) => ({
  pageContainer: css`
    padding: ${theme.spacing(3)} 0;
    min-height: 100vh;
  `.toString(),
});

export default AppointmentDetails;
